import {
	BaseEdge,
	EdgeLabelRenderer,
	EdgeProps,
	MarkerType,
	getBezierPath,
	getSimpleBezierPath,
	getSmoothStepPath,
	getStraightPath,
	useReactFlow,
} from '@xyflow/react';

import { useCallback, useEffect, useMemo, useState } from 'react';

import CheckIcon from '@mui/icons-material/Check';
import { ListItemIcon, ListItemText, Menu, MenuItem, useTheme } from '@mui/material';

import { useCopyToClipboard } from '@@common';
import { ConfirmDialog } from '@@components';

import useFlowActivityMetadata from '../../Hooks/Metadata/useFlowActivityMetadata';
import { EDGE_TYPE_OPTIONS, EdgeType, getEdgeTypeIcon } from '../../Models/EdgeTypes';
import { AGENT_TOOL_TRANSITION_TYPE, ActivityModel, Point, TransitionModel } from '../../Models/FlowModel';
import { FlowEdge } from '../../Models/GraphModel';
import { ActivityCategories } from '../ActivityIcon';
import { getExecutionTraceStrokeWidth, useFlowExecutionTraceState } from '../Contexts/FlowExecutionTraceContext';
import { ACTIVITY_HANDLES } from './ActivityHandles';
import { TransitionControlPoint } from './TransitionControlPoint';
import './TransitionEdge.scss';
import { TransitionEdgeToolbar } from './TransitionEdgeToolbar';

export const TRANSITION_EDGE_TYPE = 'transition';
// Keep arrowheads independent of the thicker strokes used for execution traces.
export const TRANSITION_MARKER_END = { type: MarkerType.ArrowClosed, markerUnits: 'userSpaceOnUse' };

export type ControlPoint = Point & {
	id: string;
};

export type EdgeMetadata = {
	edgeType?: EdgeType;
	controlPoints?: ControlPoint[];
	sourceHandle?: string;
	targetHandle?: string;
};

// Define a more specific type for our edge props
interface TransitionEdgeProps extends EdgeProps {
	data?: TransitionModel | any; // Allow more flexible typing to handle condition data
	markerStart?: any;
}

export function edgeFromTransition(model: TransitionModel, activity: ActivityModel): FlowEdge {
	const metadata = (model.Metadata || {}) as EdgeMetadata;
	const edge: FlowEdge = {
		id: model.Id,
		type: TRANSITION_EDGE_TYPE,
		source: activity.Id,
		sourceHandle: metadata.sourceHandle || ACTIVITY_HANDLES.RIGHT_SOURCE,
		target: model.To,
		targetHandle: metadata.targetHandle || ACTIVITY_HANDLES.LEFT_TARGET,
		markerEnd: TRANSITION_MARKER_END,
		data: model,
	};
	return edge;
}

function calculateEdgePath(
	sourceX: number,
	sourceY: number,
	targetX: number,
	targetY: number,
	sourcePosition: any,
	targetPosition: any,
	edgeType: EdgeType,
	controlPoints: ControlPoint[],
): { edgePath: string; labelX: number; labelY: number } {
	if (controlPoints.length > 0) {
		// Custom path through control points
		const points = [{ x: sourceX, y: sourceY }, ...controlPoints.map((cp) => ({ x: cp.x, y: cp.y })), { x: targetX, y: targetY }];

		let path = '';

		// Special handling for Bezier to create smooth curves through waypoints
		if (edgeType === EdgeType.Bezier) {
			// Create a smooth cubic Bezier curve through all points
			path = `M ${points[0].x} ${points[0].y}`;

			for (let i = 0; i < points.length - 1; i++) {
				const current = points[i];
				const next = points[i + 1];
				const prev = i > 0 ? points[i - 1] : null;
				const afterNext = i < points.length - 2 ? points[i + 2] : null;

				// Calculate control points for smooth curve
				const tension = 0.3; // Adjust this value (0-1) for curve smoothness

				// Calculate the distance between current and next point
				const segmentLength = Math.sqrt(Math.pow(next.x - current.x, 2) + Math.pow(next.y - current.y, 2));
				const controlPointDistance = segmentLength * tension;

				// Control point 1 (outgoing from current point)
				let cp1x: number, cp1y: number;
				if (prev) {
					// Direction from previous to next (tangent through current)
					const dx = next.x - prev.x;
					const dy = next.y - prev.y;
					const length = Math.sqrt(dx * dx + dy * dy);
					if (length > 0) {
						cp1x = current.x + (dx / length) * controlPointDistance;
						cp1y = current.y + (dy / length) * controlPointDistance;
					} else {
						cp1x = current.x + (next.x - current.x) * tension;
						cp1y = current.y + (next.y - current.y) * tension;
					}
				} else {
					// First segment - use direction to next point
					cp1x = current.x + (next.x - current.x) * tension;
					cp1y = current.y + (next.y - current.y) * tension;
				}

				// Control point 2 (incoming to next point)
				let cp2x: number, cp2y: number;
				if (afterNext) {
					// Direction from current to afterNext (tangent through next)
					const dx = afterNext.x - current.x;
					const dy = afterNext.y - current.y;
					const length = Math.sqrt(dx * dx + dy * dy);
					if (length > 0) {
						cp2x = next.x - (dx / length) * controlPointDistance;
						cp2y = next.y - (dy / length) * controlPointDistance;
					} else {
						cp2x = next.x - (next.x - current.x) * tension;
						cp2y = next.y - (next.y - current.y) * tension;
					}
				} else {
					// Last segment - use direction from current point
					cp2x = next.x - (next.x - current.x) * tension;
					cp2y = next.y - (next.y - current.y) * tension;
				}

				path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${next.x} ${next.y}`;
			}

			// Calculate label position at midpoint
			const midIndex = Math.floor(points.length / 2);
			const lX = points[midIndex].x;
			const lY = points[midIndex].y;

			return { edgePath: path, labelX: lX, labelY: lY };
		}

		// For other edge types, build path segment by segment
		for (let i = 0; i < points.length - 1; i++) {
			const start = points[i];
			const end = points[i + 1];

			// Determine positions (for now, assume horizontal/vertical preference)
			const isHorizontal = Math.abs(end.x - start.x) > Math.abs(end.y - start.y);
			const startPos = isHorizontal ? (end.x > start.x ? 'right' : 'left') : end.y > start.y ? 'bottom' : 'top';
			const endPos = isHorizontal ? (end.x > start.x ? 'left' : 'right') : end.y > start.y ? 'top' : 'bottom';

			let segmentPath: string;

			switch (edgeType) {
				case EdgeType.SmoothStep: {
					const [p] = getSmoothStepPath({
						sourceX: start.x,
						sourceY: start.y,
						sourcePosition: startPos as any,
						targetX: end.x,
						targetY: end.y,
						targetPosition: endPos as any,
					});
					segmentPath = p;
					break;
				}
				case EdgeType.Step: {
					const [p] = getSmoothStepPath({
						sourceX: start.x,
						sourceY: start.y,
						sourcePosition: startPos as any,
						targetX: end.x,
						targetY: end.y,
						targetPosition: endPos as any,
						borderRadius: 0,
					});
					segmentPath = p;
					break;
				}
				case EdgeType.Straight:
				default: {
					const [p] = getStraightPath({
						sourceX: start.x,
						sourceY: start.y,
						targetX: end.x,
						targetY: end.y,
					});
					segmentPath = p;
					break;
				}
			}

			// For first segment, use the full path. For subsequent segments, skip the M command
			if (i === 0) {
				path = segmentPath;
			} else {
				// Remove only the M command (move to) from subsequent segments
				// Match "M x,y " or "M x y " and remove it
				const pathWithoutMove = segmentPath.replace(/^M\s*[\d.]+[\s,]+[\d.]+\s*/, '');
				path += ' ' + pathWithoutMove;
			}
		}

		// Calculate label position at midpoint
		const midIndex = Math.floor(points.length / 2);
		const lX = points[midIndex].x;
		const lY = points[midIndex].y;

		return { edgePath: path, labelX: lX, labelY: lY };
	}

	// Use built-in edge type
	let path: string;
	let lX: number;
	let lY: number;

	switch (edgeType) {
		case EdgeType.SmoothStep: {
			const [p, x, y] = getSmoothStepPath({
				sourceX,
				sourceY,
				sourcePosition,
				targetX,
				targetY,
				targetPosition,
			});
			path = p;
			lX = x;
			lY = y;
			break;
		}
		case EdgeType.Step: {
			const [p, x, y] = getSmoothStepPath({
				sourceX,
				sourceY,
				sourcePosition,
				targetX,
				targetY,
				targetPosition,
				borderRadius: 0,
			});
			path = p;
			lX = x;
			lY = y;
			break;
		}
		case EdgeType.Straight: {
			const [p, x, y] = getStraightPath({
				sourceX,
				sourceY,
				targetX,
				targetY,
			});
			path = p;
			lX = x;
			lY = y;
			break;
		}
		case EdgeType.Bezier: {
			const [p, x, y] = getBezierPath({
				sourceX,
				sourceY,
				sourcePosition,
				targetX,
				targetY,
				targetPosition,
			});
			path = p;
			lX = x;
			lY = y;
			break;
		}
		default: {
			const [p, x, y] = getSimpleBezierPath({
				sourceX,
				sourceY,
				sourcePosition,
				targetX,
				targetY,
				targetPosition,
			});
			path = p;
			lX = x;
			lY = y;
		}
	}

	return { edgePath: path, labelX: lX, labelY: lY };
}

export default function TransitionEdge({
	id,
	source,
	target,
	sourceX,
	sourceY,
	targetX,
	targetY,
	sourcePosition,
	targetPosition,
	data,
	label,
	markerEnd,
	markerStart,
	selected,
	style,
}: TransitionEdgeProps) {
	const { setEdges, screenToFlowPosition, getNode } = useReactFlow();
	const theme = useTheme();
	const executionTrace = useFlowExecutionTraceState();

	const sourceNode = getNode(source);
	const sourceActivityType = (sourceNode?.data as ActivityModel | undefined)?.Type;
	const sourceMetadata = useFlowActivityMetadata(sourceActivityType ?? '');

	const targetNode = getNode(target);
	const targetActivityType = (targetNode?.data as ActivityModel | undefined)?.Type;
	const targetMetadata = useFlowActivityMetadata(targetActivityType ?? '');

	const isAgentToAgentService =
		(sourceMetadata?.categories?.includes(ActivityCategories.AGENT) ?? false) &&
		(targetMetadata?.categories?.includes(ActivityCategories.AGENT_SERVICE) ?? false);
	const isAgentTool = data?.Type?.toLowerCase() === AGENT_TOOL_TRANSITION_TYPE.toLowerCase();
	const executionState = executionTrace?.edgeStates.get(id);
	const executionVisitCount = executionTrace?.edgeVisitCounts.get(id) ?? 1;
	const executionStrokeWidth = getExecutionTraceStrokeWidth(executionVisitCount);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const [edgeTypeAnchor, setEdgeTypeAnchor] = useState<null | HTMLElement>(null);

	// Update zIndex when selection changes
	useEffect(() => {
		setEdges((edges) =>
			edges.map((e) => {
				if (e.id === id) {
					const newZIndex = selected ? 1000 : 0;
					return e.zIndex !== newZIndex ? { ...e, zIndex: newZIndex } : e;
				}
				return e;
			}),
		);
	}, [selected, id, setEdges]);

	// Get metadata from transition model
	const metadata = (data?.Metadata || {}) as EdgeMetadata;
	const edgeType = metadata.edgeType || EdgeType.Default;
	const controlPoints = useMemo(() => metadata.controlPoints || [], [metadata.controlPoints]);

	// Calculate edge path based on type
	// Control points are stored in screen coordinates, matching sourceX/Y and targetX/Y
	const { edgePath, labelX, labelY } = useMemo(
		() => calculateEdgePath(sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, edgeType, controlPoints),
		[sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, edgeType, controlPoints],
	);

	const handleRemove = () => {
		setEdges((edges) => edges.filter((e) => e.id !== id));
		setShowDeleteConfirm(false);
	};

	const handleRemoveClick = () => {
		setShowDeleteConfirm(true);
	};

	const handleCancelRemove = () => {
		setShowDeleteConfirm(false);
	};

	const handleEdgeTypeClick = (event: React.MouseEvent<HTMLElement>) => {
		setEdgeTypeAnchor(event.currentTarget);
	};

	const handleEdgeTypeClose = () => {
		setEdgeTypeAnchor(null);
	};

	const handleEdgeTypeSelect = (newType: EdgeType) => {
		setEdges((edges) =>
			edges.map((edge) => {
				if (edge.id === id) {
					const updatedMetadata: EdgeMetadata = {
						...(edge.data?.Metadata || {}),
						edgeType: newType,
					};
					return {
						...edge,
						data: {
							...edge.data,
							Metadata: updatedMetadata,
						},
					};
				}
				return edge;
			}),
		);
		handleEdgeTypeClose();
	};

	const copyToClipboard = useCopyToClipboard();

	const handleCopyData = useCallback(async () => {
		await copyToClipboard(data);
	}, [data, copyToClipboard]);

	const findBestInsertionIndex = useCallback(
		(newPoint: Point, existingPoints: ControlPoint[]): number => {
			// Build the full path: source -> control points -> target
			const pathPoints = [{ x: sourceX, y: sourceY }, ...existingPoints, { x: targetX, y: targetY }];

			// Find the best position to insert the new point
			// by checking which segment it's closest to
			let bestSegmentIndex = 0;
			let minDistance = Infinity;

			for (let i = 0; i < pathPoints.length - 1; i++) {
				const p1 = pathPoints[i];
				const p2 = pathPoints[i + 1];

				// Calculate distance from point to line segment
				const A = newPoint.x - p1.x;
				const B = newPoint.y - p1.y;
				const C = p2.x - p1.x;
				const D = p2.y - p1.y;

				const dot = A * C + B * D;
				const lenSq = C * C + D * D;
				let param = -1;

				if (lenSq !== 0) {
					param = dot / lenSq;
				}

				let xx, yy;

				if (param < 0) {
					xx = p1.x;
					yy = p1.y;
				} else if (param > 1) {
					xx = p2.x;
					yy = p2.y;
				} else {
					xx = p1.x + param * C;
					yy = p1.y + param * D;
				}

				const dx = newPoint.x - xx;
				const dy = newPoint.y - yy;
				const distance = Math.sqrt(dx * dx + dy * dy);

				if (distance < minDistance) {
					minDistance = distance;
					bestSegmentIndex = i;
				}
			}

			return bestSegmentIndex;
		},
		[sourceX, sourceY, targetX, targetY],
	);

	const handleAddControlPoint = useCallback(
		(event: React.MouseEvent) => {
			if (!selected) return;

			// Convert screen coordinates to flow coordinates for consistent positioning
			const flowPosition = screenToFlowPosition({ x: event.clientX, y: event.clientY });
			const newPoint: ControlPoint = {
				id: `cp-${Date.now()}`,
				x: flowPosition.x,
				y: flowPosition.y,
			};

			setEdges((edges) =>
				edges.map((edge) => {
					if (edge.id === id) {
						const currentMetadata = (edge.data?.Metadata || {}) as EdgeMetadata;
						const existingPoints = currentMetadata.controlPoints || [];

						// Find the best position to insert the new point
						const bestSegmentIndex = findBestInsertionIndex(flowPosition, existingPoints);

						// Insert the new point at the best position
						const updatedControlPoints = [...existingPoints];
						updatedControlPoints.splice(bestSegmentIndex, 0, newPoint);

						const updatedMetadata: EdgeMetadata = {
							edgeType: currentMetadata.edgeType,
							controlPoints: updatedControlPoints,
							sourceHandle: currentMetadata.sourceHandle,
							targetHandle: currentMetadata.targetHandle,
						};
						return {
							...edge,
							data: {
								...edge.data,
								Metadata: updatedMetadata,
							},
						};
					}
					return edge;
				}),
			);
		},
		[id, selected, setEdges, screenToFlowPosition, findBestInsertionIndex],
	);

	const handleControlPointDragStart = useCallback((_pointId: string) => {
		// Could be used for visual feedback if needed
	}, []);

	const handleControlPointDrag = useCallback(
		(pointId: string, screenX: number, screenY: number) => {
			// Convert screen coordinates to flow coordinates
			const flowPosition = screenToFlowPosition({ x: screenX, y: screenY });
			setEdges((edges) =>
				edges.map((edge) => {
					if (edge.id === id) {
						const currentMetadata = (edge.data?.Metadata || {}) as EdgeMetadata;
						const updatedControlPoints = (currentMetadata.controlPoints || []).map((cp) =>
							cp.id === pointId ? { ...cp, x: flowPosition.x, y: flowPosition.y } : cp,
						);
						const updatedMetadata: EdgeMetadata = {
							...currentMetadata,
							controlPoints: updatedControlPoints,
						};
						return {
							...edge,
							data: {
								...edge.data,
								Metadata: updatedMetadata,
							},
						};
					}
					return edge;
				}),
			);
		},
		[id, setEdges, screenToFlowPosition],
	);

	const handleControlPointDragEnd = useCallback(() => {
		// Drag ended
	}, []);

	const handleRemoveControlPoint = useCallback(
		(pointId: string) => {
			setEdges((edges) =>
				edges.map((edge) => {
					if (edge.id === id) {
						const currentMetadata = (edge.data?.Metadata || {}) as EdgeMetadata;
						const updatedControlPoints = (currentMetadata.controlPoints || []).filter((cp) => cp.id !== pointId);
						const updatedMetadata: EdgeMetadata = {
							...currentMetadata,
							controlPoints: updatedControlPoints,
						};
						return {
							...edge,
							data: {
								...edge.data,
								Metadata: updatedMetadata,
							},
						};
					}
					return edge;
				}),
			);
		},
		[id, setEdges],
	);

	const edgeTypeMenuOpen = Boolean(edgeTypeAnchor);

	return (
		<>
			<BaseEdge
				id={id}
				path={edgePath}
				markerEnd={markerEnd}
				markerStart={markerStart}
				style={{
					...style,
					...(isAgentToAgentService || isAgentTool ? { strokeDasharray: '6 3' } : {}),
					...(executionState === 'visited' ? { stroke: theme.palette.success.main, strokeWidth: executionStrokeWidth } : {}),
					...(executionState === 'active'
						? {
								stroke: theme.palette.success.light,
								strokeWidth: executionStrokeWidth,
								strokeDasharray: '8 5',
								animation: 'flow-edge-trace-dash 0.8s linear infinite',
							}
						: {}),
				}}
			/>
			{/* Invisible wider path for easier clicking to add control points */}
			{selected && (
				<path d={edgePath} fill="none" stroke="transparent" strokeWidth={20} onDoubleClick={handleAddControlPoint} style={{ cursor: 'crosshair' }} />
			)}
			<EdgeLabelRenderer>
				<div className="transition-edge">
					{(label || data?.Condition || isAgentTool || selected) && (
						<div className="nodrag nopan" style={{ position: 'absolute', transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`, pointerEvents: 'all' }}>
							<TransitionEdgeToolbar label={label} hasCondition={!!data?.Condition} isAgentTool={isAgentTool} selected={!!selected}
								onChangeType={handleEdgeTypeClick} onCopy={handleCopyData} onRemove={handleRemoveClick} />
						</div>
					)}

					{/* Render control points */}
					{controlPoints.map((cp) => (
						<TransitionControlPoint
							key={cp.id}
							id={cp.id}
							x={cp.x}
							y={cp.y}
							selected={selected}
							onDragStart={handleControlPointDragStart}
							onDrag={handleControlPointDrag}
							onDragEnd={handleControlPointDragEnd}
							onRemove={handleRemoveControlPoint}
						/>
					))}
				</div>
			</EdgeLabelRenderer>
			<Menu anchorEl={edgeTypeAnchor} open={edgeTypeMenuOpen} onClose={handleEdgeTypeClose}>
				{EDGE_TYPE_OPTIONS.map((option) => (
					<MenuItem key={option.value} onClick={() => handleEdgeTypeSelect(option.value)} selected={edgeType === option.value}>
						<ListItemIcon>{getEdgeTypeIcon(option.value)}</ListItemIcon>
						<ListItemText>{option.label}</ListItemText>
						{edgeType === option.value && (
							<ListItemIcon>
								<CheckIcon fontSize="small" />
							</ListItemIcon>
						)}
					</MenuItem>
				))}
			</Menu>
			<ConfirmDialog
				open={showDeleteConfirm}
				title="Remove Transition"
				message="Are you sure you want to remove this transition?"
				confirmText="Remove"
				confirmColor="error"
				onConfirm={handleRemove}
				onCancel={handleCancelRemove}
			/>
		</>
	);
}
